import { useCallback, useEffect, useMemo, useState } from 'react'
import { matchesWithPath } from '../../lib/assetPath'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiClose, mdiPlus } from '@mdi/js'
import { Button } from '../ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from '../ApiSearchableSelect'
import { apiClient } from '../../api/client'
import { useApi } from '../../api/hooks'
import { useLocale } from '../../contexts/LocaleContext'
import { getLocalizedName } from '../../lib/localization'
import { useRelationInvalidation } from './useRelationInvalidation'
import type { RelationDirection } from '../../lib/relation-grouping'
import type { components } from '../../api/schema'

type PossibleRelationTypeItem = components['schemas']['PossibleRelationTypeItem']
type AssetRelationType = components['schemas']['AssetRelationType']

/** Best-effort human message out of an openapi-fetch error body. The backend's
 *  ErrorResponse shape is `{title, code, …}` and `title` is already a full
 *  sentence (e.g. naming the conflicting asset on
 *  relation_cardinality_violated), so prefer it over the raw JSON. */
function extractErrorMessage(err: unknown): string {
    if (err && typeof err === 'object') {
        const title = (err as { title?: unknown }).title
        if (typeof title === 'string' && title) return title
    }
    return err instanceof Error ? err.message : String(err)
}

export interface AddRelationDialogProps {
    org: string
    /** The focal asset the panel belongs to. */
    assetUid: string
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Relation types this asset's type may take part in, straight from
     *  GET /asset-types/{uid}/possible-relation-types. */
    possibleTypes: PossibleRelationTypeItem[]
    /** Full org catalog indexed by code — supplies the allowed counterpart
     *  asset type codes, which the possible-types response doesn't carry. */
    catalog: Record<string, AssetRelationType>
    /** Overrides « Add a relation » (« Ajouter une servitude »...). */
    title?: string
}

/**
 * "Add a relation" dialog: pick the relation type, the direction (only when
 * the catalog says this asset type can play both sides), then one or several
 * counterpart assets.
 *
 * Everything is catalog-driven: the option list, the wording of each option,
 * the allowed counterpart asset types and whether several counterparts can be
 * picked at once all come from the API. No relation-type code is special-cased
 * here (spec 2026-08-16-03 §1/§3).
 *
 * Cardinality violations are NOT pre-hidden — the backend is the authority, so
 * a rejected POST surfaces its message inline (§3).
 */
export function AddRelationDialog({
    org,
    assetUid,
    open,
    onOpenChange,
    possibleTypes,
    catalog,
    title,
}: AddRelationDialogProps) {
    const { t } = useTranslation('assets')
    const { locale } = useLocale()
    const { request } = useApi()
    const invalidate = useRelationInvalidation(org)

    const [code, setCode] = useState<string>('')
    const [direction, setDirection] = useState<RelationDirection>('outgoing')
    const [picked, setPicked] = useState<ApiSearchableSelectOption[]>([])
    const [saving, setSaving] = useState(false)
    const [errorMsg, setErrorMsg] = useState<string | null>(null)

    const selected = useMemo(
        () => possibleTypes.find((p) => p.code === code),
        [code, possibleTypes],
    )
    const selectedCatalog = code ? catalog[code] : undefined

    /** Reset to a clean slate every time the dialog opens. */
    useEffect(() => {
        if (!open) return
        const first = possibleTypes[0]
        setCode(first?.code ?? '')
        setDirection(first?.role === 'destination' ? 'incoming' : 'outgoing')
        setPicked([])
        setErrorMsg(null)
    }, [open, possibleTypes])

    /** Keep the direction legal when the type changes. */
    useEffect(() => {
        if (!selected) return
        if (selected.role === 'source' && direction !== 'outgoing') setDirection('outgoing')
        if (selected.role === 'destination' && direction !== 'incoming') setDirection('incoming')
    }, [direction, selected])

    /** Localized name of a relation type — the phrase the option produces
     *  ("Dessert…", "Fait partie de…"), straight from the catalog. */
    const nameFor = useCallback(
        (p: PossibleRelationTypeItem): string => getLocalizedName(p.name, locale) || p.code,
        [locale],
    )

    /** The role noun the focal asset plays in a given direction — the catalog's
     *  source_role when it is the source, target_role when it is the target. */
    const roleFor = useCallback(
        (p: PossibleRelationTypeItem, dir: RelationDirection): string => {
            const roles = dir === 'outgoing' ? p.source_role : p.target_role
            return getLocalizedName(roles, locale) || nameFor(p)
        },
        [locale, nameFor],
    )

    /**
     * Several counterparts at once are allowed when the catalog says the focal
     * asset's own side is unbounded — a riser serving 12 lots is
     * `source_cardinality: many`, so 12 edges in one pass (§3.4).
     */
    const multi = useMemo(() => {
        if (!selected) return false
        return direction === 'outgoing'
            ? selected.source_cardinality !== 'one'
            : selected.target_cardinality !== 'one'
    }, [direction, selected])

    /** Counterpart asset type codes allowed by the catalog, if constrained. */
    const allowedCounterpartTypes = useMemo<string[]>(() => {
        if (!selectedCatalog) return []
        const codes = direction === 'outgoing'
            ? selectedCatalog.possible_destination_asset_type_codes
            : selectedCatalog.possible_source_asset_type_codes
        return codes ?? []
    }, [direction, selectedCatalog])

    const searchAssets = useCallback(
        async (query: string): Promise<ApiSearchableSelectOption[]> => {
            try {
                const queryParams: Record<string, string> = { limit: '50', asset_chain: 'true' }
                if (query) {
                    queryParams.q = query
                    queryParams.sort = 'name'
                } else {
                    queryParams.sort = '-updated_at'
                }
                if (allowedCounterpartTypes.length > 0) {
                    queryParams.asset_type_code = allowedCounterpartTypes.join(',')
                }
                const response = await request(
                    '/api/v1/organizations/{orgCode}/assets',
                    'GET',
                    { params: { path: { orgCode: org }, query: queryParams } },
                )
                const items = (response as { data?: Array<Record<string, any>> })?.data || []
                return matchesWithPath(items as Array<Record<string, any> & { uid: string }>)
                    .filter(({ row }) => row.uid !== assetUid)
                    .map(({ row: a, label }) => ({
                        value: a.uid as string,
                        label: (a.name ? label : a.external_ids?.code || label) as string,
                        data: a,
                    }))
            } catch (err) {
                console.error('Asset search error', err)
                return []
            }
        },
        [allowedCounterpartTypes, assetUid, org, request],
    )

    const addPicked = useCallback(
        (opt: ApiSearchableSelectOption) => {
            if (!opt.value) return
            setErrorMsg(null)
            setPicked((prev) => {
                if (prev.some((p) => p.value === opt.value)) return prev
                return multi ? [...prev, opt] : [opt]
            })
        },
        [multi],
    )

    const removePicked = useCallback((value: string) => {
        setPicked((prev) => prev.filter((p) => p.value !== value))
    }, [])

    const submit = useCallback(async () => {
        if (!code || picked.length === 0) return
        setSaving(true)
        setErrorMsg(null)
        const created: string[] = []
        try {
            for (const target of picked) {
                // The POST always hangs off the SOURCE asset, so an incoming
                // edge is simply the same call with the roles swapped.
                const sourceUid = direction === 'outgoing' ? assetUid : target.value
                const targetUid = direction === 'outgoing' ? target.value : assetUid
                const { error } = await apiClient.POST(
                    '/api/v1/organizations/{orgCode}/assets/{assetUid}/relations',
                    {
                        params: { path: { orgCode: org, assetUid: sourceUid } },
                        body: { relation_type: code, target_asset_uid: targetUid },
                    } as any,
                )
                if (error) throw error
                created.push(target.value)
            }
            invalidate([assetUid, ...picked.map((p) => p.value)])
            onOpenChange(false)
        } catch (err) {
            // Partial success is real (one POST per edge): keep the dialog
            // open on the leftovers so the user sees exactly what failed.
            invalidate([assetUid, ...created])
            setPicked((prev) => prev.filter((p) => !created.includes(p.value)))
            setErrorMsg(extractErrorMessage(err))
        } finally {
            setSaving(false)
        }
    }, [assetUid, code, direction, invalidate, onOpenChange, org, picked])

    const bothDirections = selected?.role === 'both'

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg" data-testid="add-relation-dialog">
                <DialogHeader>
                    <DialogTitle>
                        {title ?? t('relations.add_title', { defaultValue: 'Add a relation' })}
                    </DialogTitle>
                    <DialogDescription>
                        {t('relations.add_description', {
                            defaultValue:
                                'Link this asset to another one. The wording of each option comes from the relation-type catalog.',
                        })}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <label className="text-sm font-medium text-foreground">
                            {t('relations.type_label', { defaultValue: 'Relation type' })}
                        </label>
                        <Select value={code} onValueChange={setCode}>
                            <SelectTrigger data-testid="relation-type-select">
                                <SelectValue
                                    placeholder={t('relations.type_placeholder', {
                                        defaultValue: 'Pick a relation type…',
                                    })}
                                />
                            </SelectTrigger>
                            <SelectContent>
                                {possibleTypes.map((p) => (
                                    <SelectItem
                                        key={p.code}
                                        value={p.code}
                                        data-testid={`relation-type-option-${p.code}`}
                                    >
                                        {nameFor(p)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {bothDirections && (
                        <div className="space-y-1.5">
                            {/* Article-free label: the options below are raw
                                localized role nouns straight from the catalog,
                                so nothing may be prefixed to them. */}
                            <label className="text-sm font-medium text-foreground">
                                {t('relations.direction_label', {
                                    defaultValue: 'Role of this asset',
                                })}
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {(['outgoing', 'incoming'] as const).map((dir) => (
                                    <Button
                                        key={dir}
                                        type="button"
                                        size="sm"
                                        variant={direction === dir ? 'default' : 'outline'}
                                        onClick={() => {
                                            setDirection(dir)
                                            setPicked([])
                                        }}
                                        data-testid={`relation-direction-${dir}`}
                                    >
                                        {selected ? roleFor(selected, dir) : dir}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="space-y-1.5">
                        <label className="text-sm font-medium text-foreground">
                            {multi
                                ? t('relations.targets_label', { defaultValue: 'Linked assets' })
                                : t('relations.target_label', { defaultValue: 'Linked asset' })}
                        </label>
                        <ApiSearchableSelect
                            value={multi ? undefined : picked[0]?.value}
                            placeholder={t('relations.select_target_placeholder', {
                                defaultValue: 'Select an asset…',
                            })}
                            searchPlaceholder={t('relations.search_placeholder', {
                                defaultValue: 'Search by name…',
                            })}
                            emptyMessage={t('relations.no_results', {
                                defaultValue: 'No matching assets',
                            })}
                            onSearch={searchAssets}
                            onOptionSelect={addPicked}
                            disabled={!code}
                            data-testid="relation-target-search"
                        />
                        {multi && (
                            <p className="text-xs text-muted-foreground">
                                {t('relations.multi_hint', {
                                    defaultValue:
                                        'Pick several assets one after another — one link is created per asset.',
                                })}
                            </p>
                        )}
                        {picked.length > 0 && (
                            <ul
                                className="flex flex-wrap gap-1.5 pt-1"
                                data-testid="relation-picked-targets"
                            >
                                {picked.map((p) => (
                                    <li
                                        key={p.value}
                                        className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs"
                                        data-testid={`relation-picked-${p.value}`}
                                    >
                                        <span>{p.label}</span>
                                        <button
                                            type="button"
                                            onClick={() => removePicked(p.value)}
                                            aria-label={t('common:actions.remove', {
                                                defaultValue: 'Remove',
                                            })}
                                            title={t('common:actions.remove', {
                                                defaultValue: 'Remove',
                                            })}
                                            data-testid={`relation-picked-remove-${p.value}`}
                                        >
                                            <AppIcon path={mdiClose} size="xs" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {errorMsg && (
                        <div
                            className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive"
                            data-testid="relation-add-error"
                        >
                            {errorMsg}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={saving}
                        data-testid="relation-add-cancel"
                    >
                        {t('common:actions.cancel', { defaultValue: 'Cancel' })}
                    </Button>
                    <Button
                        type="button"
                        onClick={submit}
                        disabled={saving || !code || picked.length === 0}
                        data-testid="relation-add-submit"
                    >
                        <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
                        <span className="hidden md:inline">
                            {t('relations.add_submit', {
                                // Never "Add 0 links" — the empty state reads
                                // as the singular call to action.
                                count: Math.max(picked.length, 1),
                                defaultValue: 'Add link',
                            })}
                        </span>
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
